import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Box,
  Typography,
  Stack,
  Skeleton,
} from '@mui/material';
import { GridLogicOperator } from '@mui/x-data-grid';
import { infrastructureLevel, InfrastructureType } from '@nohara-dxdev/mt-common';
import { useNotifications } from '@nohara-dxdev/react-core';
import { useOidc, useScope, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { ItemEntity } from '@/types/api';

import {
  useCloneSubItem,
  useInfrastructureSubItemsList,
  useUpdateInfrastructureSubItems,
} from '../api';
import { getItemsQueryKey } from '../api/get-items';
import { useJobStatus } from '../api/get-job-status';
import { useInfrastructureItemMapping } from '../hooks/use-infrastructure-item-mapping';
import { ItemFilterValuesProvider, useItemFilterValues } from '../providers';

import { SelectItemModal } from './select-item-modal';

interface SubItemRow {
  itemId: string;
  order: number;
}

interface ManageSubItemsDialogProps {
  open: boolean;
  onClose: () => void;
  parentItemId: string;
  parentItemNumber: string;
  parentItemName: string;
  childItemId: string;
  childItemNumber: string;
  childItemName: string;
  infrastructureType: InfrastructureType;
}

export const ManageSubItemsDialog = ({
  open,
  onClose,
  parentItemId,
  parentItemNumber,
  parentItemName,
  childItemId,
  childItemNumber,
  childItemName,
  infrastructureType,
}: ManageSubItemsDialogProps) => {
  const { t } = useTranslation();
  const { ability } = useOidc();
  const { scope } = useScope();
  const notifications = useNotifications();
  const [subItems, setSubItems] = useState<SubItemRow[]>([]);
  const [isSelectItemModalOpen, setIsSelectItemModalOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<{ itemId: string; name: string } | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneItemNumber, setCloneItemNumber] = useState('');

  // Fetch existing sub-items
  const { data: existingSubItems, isLoading } = useInfrastructureSubItemsList(
    parentItemId,
    infrastructureType,
    childItemId,
    { enabled: open },
  );

  // Update mutation
  const updateSubItemsMutation = useUpdateInfrastructureSubItems({
    onSuccess: () => {
      notifications.success(
        t('infrastructure.subItems.saveSuccess', 'Sub-items saved successfully'),
      );
      onClose();
    },
    onError: () => {
      notifications.error(t('infrastructure.subItems.saveError', 'Failed to save sub-items'));
    },
  });

  // Clone mutation. MT1-1426: clone is now async — the mutation enqueues a job
  // and we poll GET /jobs/{jobId}. Once the worker completes, the new item id
  // comes back in `job.response.itemIds[0]`, which we append to the sub-items
  // list. The clone form stays open (button shows "saving") until the worker
  // lands so the sub-item appears in the same interaction.
  const queryClient = useQueryClient();
  const { orgId, projectId } = useScopeIds('org', 'project');
  const [activeCloneJobId, setActiveCloneJobId] = useState<string | null>(null);
  const cloneJobStatusQuery = useJobStatus({ jobId: activeCloneJobId });

  const cloneItemMutation = useCloneSubItem({
    mutationConfig: {
      onSuccess: (job) => {
        setActiveCloneJobId(job.id);
      },
      onError: () => {
        notifications.error(t('items.clone.error', 'Failed to clone item'));
      },
    },
  });

  // A polling error is terminal too — otherwise a failed status request would
  // keep `cloneBusy` true and lock the clone dialog controls indefinitely.
  const cloneJobInFlight =
    activeCloneJobId !== null &&
    !cloneJobStatusQuery.isError &&
    cloneJobStatusQuery.data?.status !== 'completed' &&
    cloneJobStatusQuery.data?.status !== 'failed';
  const cloneBusy = cloneItemMutation.isPending || cloneJobInFlight;

  useEffect(() => {
    if (!activeCloneJobId) return;
    // Treat a failed status request as terminal so the clone dialog unlocks.
    if (cloneJobStatusQuery.isError) {
      notifications.error(t('items.clone.error', 'Failed to clone item'));
      setActiveCloneJobId(null);
      return;
    }
    const job = cloneJobStatusQuery.data;
    if (!job) return;
    if (job.status === 'completed') {
      const newItemId = job.response?.itemIds?.[0];
      if (newItemId) {
        // Use functional setState so the order is computed against the latest
        // list, not the snapshot captured when the job was enqueued.
        setSubItems((prev) => {
          const maxOrder = prev.length > 0 ? Math.max(...prev.map((item) => item.order)) : -1;
          return [...prev, { itemId: newItemId, order: maxOrder + 1 }];
        });
      }
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      notifications.success(t('items.clone.success', 'Item cloned successfully'));
      setCloneTarget(null);
      setCloneName('');
      setCloneItemNumber('');
      setActiveCloneJobId(null);
    } else if (job.status === 'failed') {
      notifications.error(t('items.clone.error', 'Failed to clone item'));
      setActiveCloneJobId(null);
    }
  }, [
    activeCloneJobId,
    cloneJobStatusQuery.data,
    cloneJobStatusQuery.isError,
    queryClient,
    orgId,
    projectId,
    notifications,
    t,
  ]);

  const handleCloneItem = (itemId: string, itemName: string) => {
    setCloneTarget({ itemId, name: itemName });
    setCloneName(`${itemName} (copy)`);
    setCloneItemNumber('');
  };

  const handleCloneSubmit = () => {
    if (!cloneTarget || !cloneName.trim()) return;
    cloneItemMutation.mutate({
      itemId: cloneTarget.itemId,
      name: cloneName.trim(),
      ...(cloneItemNumber.trim() ? { itemNumber: cloneItemNumber.trim() } : {}),
    });
  };

  // Form for temp item selection
  const tempItemForm = useForm<{ tempSelectedItem: ItemEntity | null }>({
    defaultValues: { tempSelectedItem: null },
  });

  const tempSelectedItem = tempItemForm.watch('tempSelectedItem');

  // Get unique item IDs for fetching details
  const subItemIds = useMemo(() => {
    return subItems.map((item) => item.itemId);
  }, [subItems]);

  // Fetch item details
  const { getItem, isLoading: isLoadingItems } = useInfrastructureItemMapping({
    itemIds: subItemIds,
    enabled: subItemIds.length > 0,
  });

  // Exclude items already in the list plus parent and child
  const excludeIds = useMemo(() => {
    return [...subItemIds, parentItemId, childItemId];
  }, [subItemIds, parentItemId, childItemId]);

  // Get parent filter values to filter out roomId/systemIds/buildingId/floorId
  const parentFilterValues = useItemFilterValues();

  // Filter out roomId, systemIds, buildingId, floorId from parent filterModel array
  const filteredFilterModel = useMemo(() => {
    const parentFilterModels = parentFilterValues.filterModel ?? [];
    if (parentFilterModels.length === 0) {
      return undefined;
    }

    // Filter each filterModel, removing items with excluded fields
    const filteredFilterModels = parentFilterModels
      .map((filterModel) => {
        const filteredItems = filterModel.items.filter(
          (item) => !['roomId', 'systemIds', 'buildingId', 'floorId'].includes(item.field),
        );

        // Return filterModel only if it has items after filtering
        if (filteredItems.length === 0) {
          return null;
        }

        return {
          items: filteredItems,
          logicOperator: filterModel.logicOperator ?? GridLogicOperator.And,
        };
      })
      .filter((model): model is NonNullable<typeof model> => model !== null);

    return filteredFilterModels.length > 0 ? filteredFilterModels : undefined;
  }, [parentFilterValues]);

  // Initialize sub-items from API
  useEffect(() => {
    if (existingSubItems) {
      const sortedItems = [...existingSubItems]
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          itemId: item.itemId,
          order: item.order,
        }));
      setSubItems(sortedItems);
    }
  }, [existingSubItems]);

  // Handle temp item selection
  useEffect(() => {
    if (tempSelectedItem) {
      // Add new item to the end
      const maxOrder = subItems.length > 0 ? Math.max(...subItems.map((item) => item.order)) : -1;
      setSubItems([...subItems, { itemId: tempSelectedItem.id, order: maxOrder + 1 }]);
      tempItemForm.setValue('tempSelectedItem', null);
      setIsSelectItemModalOpen(false);
    }
  }, [tempSelectedItem, subItems, tempItemForm]);

  const handleDeleteItem = (itemId: string) => {
    const updatedItems = subItems.filter((item) => item.itemId !== itemId);
    // Reorder remaining items
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index,
    }));
    setSubItems(reorderedItems);
  };

  const handleOrderChange = (itemId: string, newOrder: number) => {
    const order = parseInt(String(newOrder), 10);
    if (isNaN(order) || order < 0) return;

    const updatedItems = subItems.map((item) =>
      item.itemId === itemId ? { ...item, order } : item,
    );
    setSubItems(updatedItems);
  };

  const handleSave = () => {
    // Sort and normalize order
    const sortedItems = [...subItems].sort((a, b) => a.order - b.order);
    const normalizedItems = sortedItems.map((item, index) => ({
      itemId: item.itemId,
      order: index,
    }));

    updateSubItemsMutation.mutate({
      parentItemId,
      infrastructureType,
      childItemId,
      subItems: normalizedItems,
    });
  };

  const handleClose = () => {
    if (!updateSubItemsMutation.isPending) {
      onClose();
      // Reset state after dialog closes
      setTimeout(() => {
        setSubItems([]);
        tempItemForm.reset();
      }, 200);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('infrastructure.subItems.title', 'Add Sub-items')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Relationship Display */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('infrastructure.relationship', 'Relationship')}:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {parentItemName} ({parentItemNumber})
                </Typography>
                <ArrowForwardIcon color="primary" />
                <Typography variant="body1">
                  {childItemName} ({childItemNumber})
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setIsSelectItemModalOpen(true);
                }}
                disabled={updateSubItemsMutation.isPending}
              >
                {t('infrastructure.subItems.addItem', 'Add Item')}
              </Button>
            </Box>

            {/* Sub-items Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 100 }}>
                      {t('infrastructure.subItems.order', 'Order')}
                    </TableCell>
                    <TableCell>{t('infrastructure.subItems.subItem', 'Sub-item')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('common.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Skeleton width="100%" height={40} />
                      </TableCell>
                    </TableRow>
                  ) : subItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {t('infrastructure.subItems.noItems', 'No sub-items added yet')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    subItems
                      .sort((a, b) => a.order - b.order)
                      .map((subItem) => {
                        const item = getItem(subItem.itemId);
                        const isItemLoading = isLoadingItems && !item;

                        const canUpdateItem = item?.itemTemplate?.projectRoleTags.some((tag) =>
                          ability?.has(
                            { action: 'update', subject: 'item', scope },
                            { itemTemplate: { projectRoleTags: [tag] } },
                          ),
                        );

                        const canCreateItem = item?.itemTemplate?.projectRoleTags.some((tag) =>
                          ability?.has(
                            { action: 'create', subject: 'item', scope },
                            { itemTemplate: { projectRoleTags: [tag] } },
                          ),
                        );

                        const canDeleteItem = item?.itemTemplate?.projectRoleTags.some((tag) =>
                          ability?.has(
                            { action: 'delete', subject: 'item', scope },
                            { itemTemplate: { projectRoleTags: [tag] } },
                          ),
                        );
                        return (
                          <TableRow key={subItem.itemId}>
                            <TableCell>
                              <TextField
                                type="number"
                                value={subItem.order}
                                onChange={(e) => {
                                  handleOrderChange(subItem.itemId, Number(e.target.value));
                                }}
                                size="small"
                                disabled={!canUpdateItem || updateSubItemsMutation.isPending}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              {isItemLoading ? (
                                <Skeleton width={200} />
                              ) : (
                                `${item?.itemNumber ?? '-'} - ${item?.name ?? '-'}`
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  handleCloneItem(subItem.itemId, item?.name ?? '');
                                }}
                                disabled={
                                  !canCreateItem || updateSubItemsMutation.isPending || cloneBusy
                                }
                                title={t('items.clone.button', 'Clone')}
                              >
                                <ContentCopyIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  handleDeleteItem(subItem.itemId);
                                }}
                                disabled={!canDeleteItem || updateSubItemsMutation.isPending}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateSubItemsMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateSubItemsMutation.isPending || isLoading}
          >
            {updateSubItemsMutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Select Item Modal */}
      <ItemFilterValuesProvider
        filterValues={{
          // Filter out roomId, systemIds, buildingId, floorId from parent context
          // We filter the parent filterModel to exclude these fields
          // and set locationIdOrAdjacentLocationIdEq to undefined to clear it
          filterModel: filteredFilterModel,
          excludeIds,
          locationIdOrAdjacentLocationIdEq: undefined, // Explicitly clear location filter
          infrastructureRelationshipRequirements: [
            `${infrastructureType}|${infrastructureLevel.sub}`,
          ],
        }}
      >
        <FormProvider {...tempItemForm}>
          <SelectItemModal
            name="tempSelectedItem"
            open={isSelectItemModalOpen}
            onClose={() => {
              setIsSelectItemModalOpen(false);
            }}
            multiValue={false}
            title={t('infrastructure.subItems.selectItem', 'Select Sub-item')}
          />
        </FormProvider>
      </ItemFilterValuesProvider>

      {/* Clone Item Dialog */}
      <Dialog
        open={!!cloneTarget}
        onClose={() => {
          if (!cloneBusy) {
            setCloneTarget(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('items.clone.title', 'Clone Item')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {t(
                'items.clone.description',
                'Create a copy of this item. All attributes will be carried over except infrastructure relationships.',
              )}
            </Typography>
            <TextField
              label={t('items.clone.name', 'Name')}
              value={cloneName}
              onChange={(e) => {
                setCloneName(e.target.value);
              }}
              fullWidth
              required
              autoFocus
              disabled={cloneBusy}
            />
            <TextField
              label={t('items.clone.itemNumber', 'Item Number (optional)')}
              value={cloneItemNumber}
              onChange={(e) => {
                setCloneItemNumber(e.target.value);
              }}
              fullWidth
              disabled={cloneBusy}
              helperText={t('items.clone.itemNumberHelp', 'Leave empty to auto-generate')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCloneTarget(null);
            }}
            disabled={cloneBusy}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCloneSubmit}
            variant="contained"
            disabled={cloneBusy || !cloneName.trim()}
          >
            {cloneBusy ? t('common.saving') : t('items.clone.button', 'Clone')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
